/**
 * Hook de raccourcis clavier. Désactive les bindings quand un input/textarea
 * est focus pour ne pas interférer avec la saisie.
 */

import { useEffect } from 'react';

export type Shortcut = {
  key: string;
  /** Description humaine pour la popup d'aide. */
  label: string;
  handler: (e: KeyboardEvent) => void;
  /** Nécessite Shift / Ctrl / Meta ? Default = aucun modifier. */
  withShift?: boolean;
};

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function useKeyboardShortcuts(shortcuts: Shortcut[], enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditable(e.target)) return;

      for (const s of shortcuts) {
        if (e.key === s.key && (!s.withShift || e.shiftKey)) {
          s.handler(e);
          return;
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcuts, enabled]);
}
